'use client';

import { useEffect, useState } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { usePathname, useSearchParams } from 'next/navigation';
import AccessErrorPage from './AccessErrorPage';
import { useAuthStore } from '@/stores/authStore';
import { useBackend } from '@/hooks/useBackend';
import { log } from '@/lib/utils/logger';
import { formatErrorMessage } from '@/lib/utils/apiErrors';
import { IBackend } from '@/api/BackendApi';
import { EMBEDDED_QUERY_PARAM } from '@/lib/embeddedQuery';
import { resolveExistingDataRouteType } from '@/lib/routing/dataRouteResolver';
import { DEEP_LINK_EXISTS_PROBE_FAILED_STATUS, isAnonymousUser } from '@/lib/auth/resourceDeepLink';

type ResourceType = 'project' | 'sample' | 'shipping' | 'publication' | 'data';

/** Props for configuring resource-based authentication */
interface WithResourceAuthProps {
  /** Type of resource requiring authentication */
  resourceType: ResourceType;
  /** Route name, used to check if there is an ID after that part of the route, ie. /data/17 vs /data */
  route: string;
}

const resourceMappings: Record<ResourceType, string[]> = {
  sample: ['sample', 'sampleId'],
  shipping: ['shipping', 'shippingId'],
  publication: ['publication', 'publicationId'],
  data: ['data', 'dataId'],
  project: ['project', 'projectId'],
};

interface AccessResult {
  hasAccess: boolean;
  error?: { status: number; message: string };
  // When checking existence endpoints, we may also want to distinguish
  // "does not exist" (404) from "exists but no access" (403).
  exists?: boolean;
}

/**
 * Higher-order component that adds resource-based authentication to a component.
 * Checks if the current user has access rights to the specified resource.
 *
 * @template P - Props type for the wrapped component
 * @param WrappedComponent - Component to wrap with authentication
 * @param resourceType - Type of resource to check access for
 * @returns A new component with resource access checking
 *
 * @example
 * ```tsx
 * const ProtectedProjectPage = withResourceAuth(ProjectPage, { resourceType: "project" });
 * ```
 */
export default function withResourceAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  { resourceType, route }: WithResourceAuthProps
) {
  return function ProtectedResourceRoute(props: P) {
    const { login, isInProgress, isInitialized, currentUser, privileges } = useAuthStore();
    const [isChecking, setIsChecking] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [error, setError] = useState<{ status?: number; message?: string } | undefined>();
    const backend = useBackend();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    useEffect(() => {
      const checkAccess = async () => {
        if (!isInitialized || isInProgress) {
          return;
        }

        // Reset state at the start of each check
        setIsChecking(true);
        setError(undefined);
        setHasAccess(false);

        try {
          // Don't enforce EPPN here. Either anonymous access is allowed =>
          // then we have a set of privileges in the JWT to check against later in this function,
          // or we would never even get here.

          // Split the pathname into parts and remove any empty strings
          const pathParts = pathname.split('/').filter(Boolean);
          const routeIndex = pathParts.indexOf(route);
          // `embedded=true` changes layout behavior only; it should not be treated
          // as a resource selector that converts a listing page into detail mode.
          const effectiveSearchParams = new URLSearchParams(searchParams.toString());
          effectiveSearchParams.delete(EMBEDDED_QUERY_PARAM);

          // Check if this is a listing page, ie. the path is ending with general route like "projects" and no query params with an id are present.
          const isListingPage =
            // Ensure first occurrence is the last, filtering out /data/data (a data set called data is requested)
            routeIndex === pathParts.length - 1 && effectiveSearchParams.size === 0;

          if (isListingPage) {
            // TEMPORARY FIX: Allow access to listing pages regardless of privileges
            // Users without privileges can access the search/grid interface but won't find any data
            // TODO: Remove this temporary fix once proper privilege handling is implemented.

            // It is only a bit annoying (don't see anything, have to clieck to sign in, directly sending the user there is better),
            // no security risk.
            log.info(
              `[withResourceAuth] TEMPORARY: Allowing access to ${resourceType} listing page without privilege check`
            );
            setHasAccess(true);
            setIsChecking(false);
            return;

            // ORIGINAL CODE (commented out for temporary fix):
            // // For listing pages, check if user has any access to the resource type
            // if (resourceType === 'project') {
            //   // Use the current user's access information from the auth store
            //   const hasAnyAccess = currentUser?.hasAnyProjectAccess ?? false;
            //   setHasAccess(hasAnyAccess);
            // } else if (resourceType === 'sample') {
            //   // Use the current user's access information from the auth store
            //   const hasAnyAccess = currentUser?.hasAnySampleAccess ?? false;
            //   setHasAccess(hasAnyAccess);
            // } else {
            //   // For other resource types, the middleware would not let us get here if the user does not have access
            //   setHasAccess(true);
            // }
            // setIsChecking(false);
            // return;
          }

          // For specific resources, check access rights
          // identifiers can be found in routes (/samples/123) or in query params (/projects?shipping=17), or both
          // We might have more than one resource to check.
          const accessChecks: Promise<boolean>[] = [];

          // 1.) Identifier from the route (path)
          // For the data route, those can be DOIs, possibly with slashes in the path (everything after /data/).
          const id =
            route === 'data' || route === 'publications'
              ? routeIndex >= 0 && pathParts.length > routeIndex + 1
                ? pathParts.slice(routeIndex + 1).join('/')
                : undefined
              : pathParts.length > routeIndex + 1
                ? pathParts[routeIndex + 1]
                : undefined;
          if (id) {
            const result = await hasReadAccess(resourceType, backend, decodeURIComponent(id));
            if (!result.hasAccess) {
              if (isAnonymousUser(currentUser)) {
                setHasAccess(false);
                setIsChecking(false);
                return;
              }
              setError(
                result.error ?? {
                  status: DEEP_LINK_EXISTS_PROBE_FAILED_STATUS,
                }
              );
              setIsChecking(false);
              return;
            }
            accessChecks.push(Promise.resolve(result.hasAccess));
          }

          // 2.) and check query parameters also, using the mapping defined above
          for (const [resourceType, keys] of Object.entries(resourceMappings) as [ResourceType, string[]][]) {
            const id = keys.map((key) => effectiveSearchParams.get(key)).find((value) => value && value.trim() !== '');

            if (id) {
              const result = await hasReadAccess(resourceType, backend, decodeURIComponent(id));
              if (!result.hasAccess) {
                if (isAnonymousUser(currentUser)) {
                  setHasAccess(false);
                  setIsChecking(false);
                  return;
                }
                setError(
                  result.error ?? {
                    status: DEEP_LINK_EXISTS_PROBE_FAILED_STATUS,
                  }
                );
                setIsChecking(false);
                return;
              }
              accessChecks.push(Promise.resolve(result.hasAccess));
            }
          }

          // We should not be here if there are no identifiers at all.
          // Maybe with something like /samples?RandomNoise, but that's a bad request => 400.

          if (accessChecks.length === 0) {
            setError({
              status: 400,
              message: 'No valid resource identifier found',
            });
            setIsChecking(false);
            return;
          }

          // Wait for all access checks to complete
          const results = await Promise.all(accessChecks);
          const allPassed = results.every((result) => result);
          if (!allPassed) {
            setError({ status: 403, message: 'Access denied' });
          }
          setHasAccess(allPassed);
          setIsChecking(false);
        } catch (error) {
          log.error('[withResourceAuth] Error checking access:', {
            error:
              error instanceof Error
                ? {
                    name: error.name,
                    message: formatErrorMessage(error),
                    stack: error.stack,
                    status: 'status' in error ? error.status : undefined,
                  }
                : formatErrorMessage(error),
            pathname,
            resourceType,
          });
          // Preserve backend status codes where possible (e.g. 404 vs 403).
          const status =
            error && typeof error === 'object' && 'status' in error
              ? ((error as { status?: number }).status ?? 403)
              : 403;
          if (isAnonymousUser(currentUser) && (status === 401 || status === 403 || status === 404)) {
            setHasAccess(false);
          } else {
            setError({
              status,
              message: status === 404 ? 'Resource Not Found' : 'Access denied',
            });
            setHasAccess(false);
          }
          setIsChecking(false);
        }
      };

      void checkAccess();
    }, [isInitialized, isInProgress, pathname, searchParams, backend, currentUser, login, privileges]);

    if (isChecking) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <AccessErrorPage error={error} />;
    }

    if (!hasAccess) {
      return <AccessErrorPage />;
    }

    return <WrappedComponent {...props} />;
  };
}

/**
 *
 * Check if the current user has read access to resourceType with ID identifier.
 *
 * @param resourceType
 * @param backend
 * @param identifier
 * @returns
 */
async function hasReadAccess(resourceType: ResourceType, backend: IBackend, identifier: string): Promise<AccessResult> {
  let result: { exists?: boolean } | undefined;
  switch (resourceType) {
    case 'project':
      result = await backend.project.exists(identifier);
      break;
    case 'data': {
      const resolvedType = await resolveExistingDataRouteType(backend, identifier);
      const exists = resolvedType !== null;
      return { hasAccess: exists, exists };
    }
    case 'sample':
      result = await backend.sample.exists(identifier);
      break;
    case 'shipping': {
      const numericId = parseInt(identifier, 10);
      if (isNaN(numericId)) {
        // Invalid shipping ID format
        return { hasAccess: false };
      }
      result = await backend.shipping.exists(numericId);
      break;
    }
    case 'publication':
      result = await backend.publication.exists(identifier);
      break;
  }
  const exists = result?.exists ?? false;
  return { hasAccess: exists, exists };
}
